import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Plugin,
    Title,
    Tooltip,
    TooltipItem,
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'
import { useNavigate } from 'react-router-dom'
import {
    FaSyncAlt,
    FaFileImport,
    FaPaperclip,
    FaTimes,
    FaChevronRight,
    FaChevronLeft,
    FaChevronDown,
    FaTrashAlt,
    FaFileExcel,
    FaEnvelope,
} from 'react-icons/fa'
import { ClaimDetailDrawer } from '../omnibusShared/claimDetailDrawer'
import { formatClientName, formatDate, formatMoney } from '../omnibusShared/formatting'
import { useIsAdmin } from '../omnibusShared/useIsAdmin'
import { OmnibusCommsUploadView } from '../omnibusCommsUploadView'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Notify } from 'notiflix/build/notiflix-notify-aio'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api, { apiDownload, apiUpload } from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend)

type GroupSort =
    'claims_desc' | 'claims_asc' | 'clients_desc' | 'value_desc' | 'name_asc' | 'name_desc' | 'captured_desc'

const GROUP_SORTS: { value: GroupSort; label: string }[] = [
    { value: 'claims_desc', label: 'Most claims' },
    { value: 'claims_asc', label: 'Fewest claims' },
    { value: 'clients_desc', label: 'Most clients' },
    { value: 'value_desc', label: 'Highest value' },
    { value: 'captured_desc', label: 'Most data captured' },
    { value: 'name_asc', label: 'Lender A–Z' },
    { value: 'name_desc', label: 'Lender Z–A' },
]

// Fixed number of bars per chart page. The card height stays constant, which
// avoids Chart.js keeping a stale canvas size when the container would grow.
const LENDER_CHART_PAGE_SIZE = 10

const CAPTURE_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'Any capture state' },
    { value: 'captured', label: 'Fully captured' },
    { value: 'partial', label: 'Partly captured' },
    { value: 'none', label: 'Not captured' },
]

interface OmnibusGroupRow {
    id: number
    omnibus_ref: string
    defendant: string
    sequence_no: number
    status: string
    notes: string | null
    created_at: string | null
    updated_at: string | null
    claim_count: number
    client_count: number
    captured_count: number
    total_quantum: number | null
    average_quantum: number | null
}

interface OmnibusSummary {
    total_groups: number
    total_claims: number
    total_distinct_clients: number
    clients_in_multiple_omnibuses: number
    clients_by_omnibus_count: { omnibus_count: number; client_count: number }[]
    claims_by_defendant: {
        defendant: string
        claim_count: number
        client_count: number
        group_count: number
    }[]
    claims_by_omnibus: {
        omnibus_id: number
        omnibus_ref: string
        defendant: string
        claim_count: number
        client_count: number
    }[]
}

interface OmnibusClaimMember {
    id: number
    funder_case_ref: string
    funder_client_id: number
    joined_at: string | null
    first_name: string | null
    last_name: string | null
    email_address: string | null
    has_detail: boolean
    agreement_number: string | null
    quantum_individual: number | string | null
    loan_value: number | string | null
    monthly_repayment: number | string | null
    contract_term: string | null
    agreement_start_date: string | null
    ate_policy_number: string | null
    introducer: string | null
    batch_group: string | null
    enrichment_status: string | null
    document_count: number
    client_claim_count?: number
}

type ResetMode = 'clear_group' | 'delete_group' | 'wipe_all'

interface OmnibusResetPreview {
    mode: ResetMode
    groups: { id: number; omnibus_ref: string; defendant: string }[]
    groups_affected: number
    groups_deleted: number
    claims: number
    clients: number
    details: number
    documents: number
    schedules: number
}

const RESET_MODES: { value: ResetMode; label: string; hint: string }[] = [
    {
        value: 'clear_group',
        label: 'Empty one lender omnibus',
        hint: 'Removes every claim, captured record and document link from the group, but keeps the group itself so its reference stays the same.',
    },
    {
        value: 'delete_group',
        label: 'Delete one lender omnibus',
        hint: 'Removes the group entirely along with all its claims. A fresh group is created next time you import claims for that lender.',
    },
    {
        value: 'wipe_all',
        label: 'Wipe every omnibus',
        hint: 'Deletes all lender groups and everything inside them, so the next import rebuilds the whole structure from scratch.',
    },
]

// Headline numbers for the inline row expansion. The full picture lives on the
// group detail page; this is only enough to decide whether to click through.
interface OmnibusMemberStats {
    claim_count: number
    client_count: number
    captured_count: number
    clients_with_multiple_claims: number
    total_quantum: number | null
    average_quantum: number | null
    highest_quantum: number | null
    total_loan_value: number | null
    last_joined_at: string | null
}

const MEMBER_PREVIEW_LIMIT = 10

// Muted, low-saturation palette so the charts read as reporting rather than
// dashboard decoration.
const CHART_COLORS = ['#5b7089', '#7d8fa3', '#9aa8b8', '#b3bdc9', '#8a7f6f', '#a89a86', '#6b7f7a', '#93a39d']

interface OmnibusImportResult {
    batch_claims_total: number
    batch_clients_total: number
    eligible_claims: number
    unassignable_claims: number
    groups_created: number
    claims_imported: number
    clients_imported: number
    claims_already_in_omnibus: number
    claims_failed: number
    lenders_touched: number
    claims_in_omnibuses: number
    clients_in_omnibuses: number
    no_longer_eligible: number
    details_captured: number
    documents_captured: number
    claims_enriched: number
    lead_data_missing: number
    enrichment_errors: number
    details_remaining: number
}

interface OmnibusImportPreviewLender {
    defendant: string
    omnibus_ref: string | null
    group_exists: boolean
    claims_to_import: number
    claims_already_in: number
    clients_to_import: number
}

interface OmnibusImportPreview {
    batch_claims_total: number
    batch_clients_total: number
    eligible_claims: number
    unassignable_claims: number
    claims_to_import: number
    claims_already_in_omnibus: number
    clients_to_import: number
    groups_to_create: number
    lenders_touched: number
    lenders_total: number
    by_lender: OmnibusImportPreviewLender[]
}

/** Compact pill labels for the import picker — full wording is on the admin page. */
const IMPORT_STATUS_LABELS: Record<string, string> = {
    'API transfer completed': 'API transferred',
    'Submission sheet submitted': 'Sheet submitted',
    'ATE collected': 'ATE collected',
    'ATE requested': 'ATE requested',
    'Not started': 'Not started',
}

interface OmnibusImportBatchOption {
    batch_group: string
    nera_id: string | null
    introducers: string | null
    is_funder_additional: boolean
    hidden: boolean
    batch_status: string
    funding_claims: number
    funding_clients: number
    claims_in_omnibus: number
    claims_to_import: number
    unassignable_claims: number
}

type EmailImportStatus = 'imported' | 'duplicate' | 'failed'

interface EmailImportFileResult {
    filename: string
    status: EmailImportStatus
    message?: string | null
    defendants?: Array<string | { defendant?: string; name?: string }>
    groups?: Array<string | { omnibus_ref?: string; defendant?: string; name?: string }>
}

interface EmailImportResult {
    imported: number
    duplicate: number
    failed: number
    files: EmailImportFileResult[]
}

const emptySummary = (): OmnibusSummary => ({
    total_groups: 0,
    total_claims: 0,
    total_distinct_clients: 0,
    clients_in_multiple_omnibuses: 0,
    clients_by_omnibus_count: [],
    claims_by_defendant: [],
    claims_by_omnibus: [],
})

export const OmnibusClaimsView = () => {
    const navigate = useNavigate()
    const [groups, setGroups] = useState<OmnibusGroupRow[]>([])
    const [summary, setSummary] = useState<OmnibusSummary>(emptySummary())
    const [filter, setFilter] = useState('')
    const [groupSort, setGroupSort] = useState<GroupSort>('claims_desc')
    const [statusFilter, setStatusFilter] = useState('')
    const [captureFilter, setCaptureFilter] = useState('')
    const [importOpen, setImportOpen] = useState(false)
    const [commsUploadOpen, setCommsUploadOpen] = useState(false)
    // The "Omnibus data" tools (import, re-capture, reset) and document
    // counts/links are Sentinel-admin only. The backend 403s non-admins on
    // every one of those endpoints; this keeps them out of the page.
    const isAdmin = useIsAdmin()
    const [multiClientChartsOpen, setMultiClientChartsOpen] = useState(false)
    const [lenderChartOpen, setLenderChartOpen] = useState(true)
    const [lenderChartPage, setLenderChartPage] = useState(0)
    const [loading, setLoading] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [membersLoading, setMembersLoading] = useState(false)
    const [claims, setClaims] = useState<OmnibusClaimMember[]>([])
    const [memberStats, setMemberStats] = useState<OmnibusMemberStats | null>(null)
    const [importing, setImporting] = useState(false)
    const [importStep, setImportStep] = useState<'select' | 'preview'>('select')
    const [importBatches, setImportBatches] = useState<OmnibusImportBatchOption[]>([])
    const [importBatchesLoading, setImportBatchesLoading] = useState(false)
    const [selectedBatches, setSelectedBatches] = useState<Set<string>>(new Set())
    const [importResult, setImportResult] = useState<OmnibusImportResult | null>(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [preview, setPreview] = useState<OmnibusImportPreview | null>(null)
    const [detailClaim, setDetailClaim] = useState<OmnibusClaimMember | null>(null)
    const [resetOpen, setResetOpen] = useState(false)
    const [resetMode, setResetMode] = useState<ResetMode>('clear_group')
    const [resetGroupId, setResetGroupId] = useState<number | null>(null)
    const [resetPreview, setResetPreview] = useState<OmnibusResetPreview | null>(null)
    const [resetPreviewLoading, setResetPreviewLoading] = useState(false)
    const [resetConfirmText, setResetConfirmText] = useState('')
    const [resetting, setResetting] = useState(false)
    const [capturingIds, setCapturingIds] = useState<number[]>([])
    const [emailFiles, setEmailFiles] = useState<File[]>([])
    const [emailImporting, setEmailImporting] = useState(false)
    const [emailImportResult, setEmailImportResult] = useState<EmailImportResult | null>(null)
    const emailInputRef = useRef<HTMLInputElement>(null)

    const fetchGroups = useCallback(async (opts?: { silent?: boolean }) => {
        setLoading(true)
        setError(null)
        if (!opts?.silent) Loading.circle('Loading omnibus groups...')
        try {
            const response = await api.post('omnibus-groups', {})
            if (!response.data?.success) {
                const message = response.data?.message || 'Could not load omnibus groups.'
                setError(message)
                Report.failure('Error', message, 'Okay')
                return
            }
            setGroups(response.data.groups || [])
            setSummary(response.data.summary || emptySummary())
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to load omnibus groups.'
            setError(message)
            Report.failure('Error', message, 'Okay')
        } finally {
            if (!opts?.silent) Loading.remove()
            setLoading(false)
        }
    }, [])

    const exportGroups = useCallback(async () => {
        setExporting(true)
        try {
            const response = await apiDownload('omnibus-groups-export', {})
            const contentDisposition = response.headers['content-disposition'] || ''
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
            const fallbackDate = new Date().toISOString().slice(0, 10)
            const filename = filenameMatch?.[1] || `omnibus-claims-${fallbackDate}.xlsx`
            const blobUrl = URL.createObjectURL(response.data)
            const anchor = document.createElement('a')
            anchor.href = blobUrl
            anchor.download = filename
            document.body.appendChild(anchor)
            anchor.click()
            document.body.removeChild(anchor)
            URL.revokeObjectURL(blobUrl)
            Notify.success('Omnibus Excel export downloaded.', { timeout: 3000 })
        } catch (err: any) {
            Report.failure(
                'Export failed',
                err?.message || 'Failed to export omnibus groups.',
                'Okay',
            )
        } finally {
            setExporting(false)
        }
    }, [])

    const runImport = useCallback(
        async (refreshDetails = false, batchGroups?: string[]) => {
            setImporting(true)
            setPreviewOpen(false)
            Loading.circle(
                refreshDetails
                    ? 'Re-capturing claim data and documents...'
                    : 'Importing selected funding-batch claims...',
            )
            try {
                const response = await api.post('omnibus-import-eligible', {
                    refresh_details: refreshDetails,
                    ...(batchGroups?.length ? { batch_groups: batchGroups } : {}),
                })
                if (!response.data?.success) {
                    Report.failure(
                        'Import failed',
                        response.data?.message || 'Could not import eligible claims.',
                        'Okay',
                    )
                    return
                }
                const result = response.data.result as OmnibusImportResult
                setImportResult(result)
                setPreview(null)
                Report.success(
                    refreshDetails ? 'Re-capture complete' : 'Import complete',
                    [
                        `${result.claims_imported} claim(s) added, ${result.claims_already_in_omnibus} already in an omnibus`,
                        `${result.details_captured} record(s) captured with ${result.documents_captured} document link(s)`,
                        `${result.claims_in_omnibuses} of ${result.eligible_claims} eligible claims now assigned`,
                    ].join('\n'),
                    'Okay',
                )
                await fetchGroups({ silent: true })
            } catch (err: any) {
                Report.failure(
                    'Import failed',
                    err?.response?.data?.message || err?.message || 'Failed to import eligible claims.',
                    'Okay',
                )
            } finally {
                Loading.remove()
                setImporting(false)
            }
        },
        [fetchGroups],
    )

    /** Step 1: open the modal on the batch picker. */
    const openImportModal = useCallback(async () => {
        setPreviewOpen(true)
        setImportStep('select')
        setPreview(null)
        setSelectedBatches(new Set())
        setImportBatchesLoading(true)
        try {
            const response = await api.post('omnibus-import-batches', {})
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load funding batches.', 'Okay')
                setPreviewOpen(false)
                return
            }
            setImportBatches((response.data.batches || []) as OmnibusImportBatchOption[])
        } catch (err: any) {
            Report.failure(
                'Error',
                err?.response?.data?.message || err?.message || 'Failed to load funding batches.',
                'Okay',
            )
            setPreviewOpen(false)
        } finally {
            setImportBatchesLoading(false)
        }
    }, [])

    const toggleBatchSelection = useCallback((batchGroup: string) => {
        setSelectedBatches((current) => {
            const next = new Set(current)
            if (next.has(batchGroup)) next.delete(batchGroup)
            else next.add(batchGroup)
            return next
        })
    }, [])

    /** Step 2: dry-run preview scoped to the chosen batches. */
    const goToImportPreview = useCallback(async () => {
        const groups = Array.from(selectedBatches)
        if (groups.length === 0) return
        setImportStep('preview')
        setPreview(null)
        setPreviewLoading(true)
        try {
            const response = await api.post('omnibus-import-preview', { batch_groups: groups })
            if (!response.data?.success) {
                Report.failure('Preview failed', response.data?.message || 'Could not load import preview.', 'Okay')
                setImportStep('select')
                return
            }
            setPreview(response.data.preview as OmnibusImportPreview)
        } catch (err: any) {
            Report.failure(
                'Preview failed',
                err?.response?.data?.message || err?.message || 'Failed to load import preview.',
                'Okay',
            )
            setImportStep('select')
        } finally {
            setPreviewLoading(false)
        }
    }, [selectedBatches])

    const importDefendantEmails = useCallback(async () => {
        if (emailFiles.length === 0) return
        setEmailImporting(true)
        setEmailImportResult(null)
        try {
            const formData = new FormData()
            emailFiles.forEach((file) => formData.append('emails', file))
            const response = await apiUpload('omnibus-communications-import', formData)
            if (response.data?.success === false) {
                throw new Error(response.data?.message || 'Could not import defendant emails.')
            }

            const payload = response.data?.result || response.data || {}
            const files = (payload.files || payload.results || response.data?.files || []).map(
                (row: any, index: number): EmailImportFileResult => ({
                    filename: row.filename || row.file_name || emailFiles[index]?.name || `Email ${index + 1}`,
                    status:
                        row.status === 'duplicate' || row.duplicate
                            ? 'duplicate'
                            : row.status === 'failed' || row.error
                              ? 'failed'
                              : 'imported',
                    message: row.message || row.error || null,
                    defendants: row.defendants || row.matched_defendants || [],
                    groups: row.groups || row.matched_groups || [],
                }),
            )
            const countStatus = (status: EmailImportStatus) =>
                files.filter((file: EmailImportFileResult) => file.status === status).length
            setEmailImportResult({
                imported: Number(payload.imported ?? payload.imported_count ?? countStatus('imported')),
                duplicate: Number(
                    payload.duplicate ?? payload.duplicates ?? payload.duplicate_count ?? countStatus('duplicate'),
                ),
                failed: Number(payload.failed ?? payload.failed_count ?? countStatus('failed')),
                files,
            })
            setEmailFiles([])
            if (emailInputRef.current) emailInputRef.current.value = ''
            Notify.success('Defendant email import complete.', { timeout: 3000 })
        } catch (err: any) {
            Report.failure(
                'Email import failed',
                err?.response?.data?.message || err?.message || 'Failed to import defendant emails.',
                'Okay',
            )
        } finally {
            setEmailImporting(false)
        }
    }, [emailFiles])

    const closeImportPreview = useCallback(() => {
        if (importing) return
        setPreviewOpen(false)
        setPreview(null)
        setImportStep('select')
        setSelectedBatches(new Set())
    }, [importing])

    const confirmRecapture = useCallback(() => {
        Confirm.show(
            'Re-capture claim data',
            'This re-reads every claim in every omnibus and overwrites the captured data and document links with the current values. Use after audits, quantum recalculations or document regeneration. Continue?',
            'Re-capture',
            'Cancel',
            () => {
                runImport(true)
            },
            () => undefined,
        )
    }, [runImport])

    const openReset = useCallback(() => {
        setResetOpen(true)
        setResetMode('clear_group')
        setResetPreview(null)
        setResetConfirmText('')
        setResetGroupId((current) => current ?? selectedId)
    }, [selectedId])

    const closeReset = useCallback(() => {
        if (resetting) return
        setResetOpen(false)
        setResetPreview(null)
        setResetConfirmText('')
    }, [resetting])

    const runReset = useCallback(async () => {
        setResetting(true)
        Loading.circle('Resetting omnibus data...')
        try {
            const response = await api.post('omnibus-reset', {
                mode: resetMode,
                omnibus_id: resetMode === 'wipe_all' ? undefined : resetGroupId,
                confirm: resetMode === 'wipe_all' ? 'RESET' : undefined,
            })
            if (!response.data?.success) {
                Report.failure('Reset failed', response.data?.message || 'Could not reset omnibus data.', 'Okay')
                return
            }
            Report.success('Reset complete', response.data.message, 'Okay')
            setResetOpen(false)
            setResetPreview(null)
            setResetConfirmText('')
            setImportResult(null)
            setSelectedId(null)
            await fetchGroups({ silent: true })
        } catch (err: any) {
            Report.failure(
                'Reset failed',
                err?.response?.data?.message || err?.message || 'Failed to reset omnibus data.',
                'Okay',
            )
        } finally {
            Loading.remove()
            setResetting(false)
        }
    }, [resetMode, resetGroupId, fetchGroups])

    const fetchMembers = useCallback(async (omnibusId: number) => {
        setMembersLoading(true)
        try {
            const response = await api.post('omnibus-group-members', {
                omnibus_id: omnibusId,
                limit: MEMBER_PREVIEW_LIMIT,
            })
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load members.', 'Okay')
                setClaims([])
                setMemberStats(null)
                return
            }
            setClaims(response.data.claims || [])
            setMemberStats(response.data.stats || null)
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || err?.message || 'Failed to load members.', 'Okay')
            setClaims([])
            setMemberStats(null)
        } finally {
            setMembersLoading(false)
        }
    }, [])

    const openClaimDetail = useCallback((claim: OmnibusClaimMember) => {
        setDetailClaim(claim)
    }, [])

    const closeClaimDetail = useCallback(() => {
        setDetailClaim(null)
    }, [])

    // Capture runs in the background: the row shows its own progress and the
    // rest of the page stays usable, so no blocking overlay or modal here.
    const captureGroup = useCallback(
        async (group: OmnibusGroupRow) => {
            const awaiting = Math.max(group.claim_count - group.captured_count, 0)
            if (awaiting === 0) return

            setCapturingIds((current) => [...current, group.id])
            Notify.info(`Capturing ${group.defendant}…`, { timeout: 2000 })
            try {
                const response = await api.post('omnibus-capture-group', {
                    omnibus_id: group.id,
                })
                if (!response.data?.success) {
                    throw new Error(response.data?.message || 'Capture failed')
                }
                const result = response.data.result
                const remaining = Number(result.remaining) || 0
                Notify.success(
                    `${group.defendant}: ${Number(result.details_written || 0).toLocaleString('en-GB')} captured${
                        remaining > 0 ? `, ${remaining.toLocaleString('en-GB')} left` : ''
                    }`,
                    { timeout: 5000 },
                )
                await fetchGroups({ silent: true })
                if (selectedId === group.id) {
                    await fetchMembers(group.id)
                }
            } catch (err: any) {
                Notify.failure(
                    `${group.defendant}: ${err?.response?.data?.message || err?.message || 'capture failed'}`,
                    { timeout: 6000 },
                )
            } finally {
                setCapturingIds((current) => current.filter((id) => id !== group.id))
            }
        },
        [fetchGroups, fetchMembers, selectedId],
    )

    useEffect(() => {
        fetchGroups()
    }, [fetchGroups])

    useEffect(() => {
        setDetailClaim(null)
        if (selectedId == null) {
            setClaims([])
            setMemberStats(null)
            return
        }
        fetchMembers(selectedId)
    }, [selectedId, fetchMembers])

    // Keep the reset preview in step with the chosen scope so the confirm
    // button always reflects what is actually about to be deleted.
    useEffect(() => {
        if (!resetOpen) return undefined
        const scoped = resetMode !== 'wipe_all'
        if (scoped && resetGroupId == null) {
            setResetPreview(null)
            return undefined
        }

        let cancelled = false
        setResetPreviewLoading(true)
        api.post('omnibus-reset-preview', {
            mode: resetMode,
            omnibus_id: scoped ? resetGroupId : undefined,
        })
            .then((response) => {
                if (cancelled) return
                setResetPreview(response.data?.success ? response.data.preview : null)
            })
            .catch(() => {
                if (!cancelled) setResetPreview(null)
            })
            .finally(() => {
                if (!cancelled) setResetPreviewLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [resetOpen, resetMode, resetGroupId])

    const filteredGroups = useMemo(() => {
        const q = filter.trim().toLowerCase()
        let rows = q
            ? groups.filter(
                  (g) =>
                      g.defendant.toLowerCase().includes(q) ||
                      g.omnibus_ref.toLowerCase().includes(q) ||
                      g.status.toLowerCase().includes(q),
              )
            : [...groups]

        if (statusFilter) {
            rows = rows.filter((g) => g.status === statusFilter)
        }

        if (captureFilter === 'captured') {
            rows = rows.filter((g) => g.captured_count > 0 && g.captured_count >= g.claim_count)
        } else if (captureFilter === 'partial') {
            rows = rows.filter((g) => g.captured_count > 0 && g.captured_count < g.claim_count)
        } else if (captureFilter === 'none') {
            rows = rows.filter((g) => g.captured_count === 0)
        }

        const byName = (a: OmnibusGroupRow, b: OmnibusGroupRow) =>
            a.defendant.localeCompare(b.defendant, 'en-GB', { sensitivity: 'base' })

        const comparators: Record<GroupSort, (a: OmnibusGroupRow, b: OmnibusGroupRow) => number> = {
            claims_desc: (a, b) => b.claim_count - a.claim_count || byName(a, b),
            claims_asc: (a, b) => a.claim_count - b.claim_count || byName(a, b),
            clients_desc: (a, b) => b.client_count - a.client_count || byName(a, b),
            value_desc: (a, b) => (b.total_quantum ?? -1) - (a.total_quantum ?? -1) || byName(a, b),
            name_asc: byName,
            name_desc: (a, b) => byName(b, a),
            captured_desc: (a, b) => b.captured_count - a.captured_count || byName(a, b),
        }

        return rows.sort(comparators[groupSort] || comparators.claims_desc)
    }, [groups, filter, statusFilter, captureFilter, groupSort])

    const statusOptions = useMemo(() => Array.from(new Set(groups.map((g) => g.status))).sort(), [groups])

    // Inline mini-view for the expanded lender row: headline stats plus the
    // most recent handful of claims, each row carrying its own client details.
    const renderGroupPreview = (group: OmnibusGroupRow) => {
        const stats: { label: string; value: string }[] = memberStats
            ? [
                  { label: 'Claims', value: memberStats.claim_count.toLocaleString('en-GB') },
                  { label: 'Clients', value: memberStats.client_count.toLocaleString('en-GB') },
                  {
                      label: 'Clients with 2+ claims',
                      value: memberStats.clients_with_multiple_claims.toLocaleString('en-GB'),
                  },
                  { label: 'Total quantum', value: formatMoney(memberStats.total_quantum) },
                  { label: 'Average quantum', value: formatMoney(memberStats.average_quantum) },
                  { label: 'Highest quantum', value: formatMoney(memberStats.highest_quantum) },
                  { label: 'Total loan value', value: formatMoney(memberStats.total_loan_value) },
                  {
                      label: 'Data captured',
                      value: `${memberStats.captured_count.toLocaleString(
                          'en-GB',
                      )} of ${memberStats.claim_count.toLocaleString('en-GB')}`,
                  },
                  { label: 'Latest claim added', value: formatDate(memberStats.last_joined_at) },
              ]
            : []

        return (
            <div className={styles.miniView}>
                <div className={styles.miniHeader}>
                    <h4 className={styles.miniTitle}>
                        {group.defendant} <span className={styles.refCode}>{group.omnibus_ref}</span>
                    </h4>
                    <button
                        type="button"
                        className={styles.viewBtn}
                        onClick={() => navigate(`/omnibus-claims/${group.id}`)}
                    >
                        Open omnibus <FaChevronRight />
                    </button>
                </div>

                {membersLoading && <div className={styles.emptyState}>Loading omnibus preview…</div>}

                {!membersLoading && stats.length > 0 && (
                    <div className={styles.miniStats}>
                        {stats.map((stat) => (
                            <div key={stat.label} className={styles.miniStat}>
                                <span className={styles.miniStatValue}>{stat.value}</span>
                                <span className={styles.miniStatLabel}>{stat.label}</span>
                            </div>
                        ))}
                    </div>
                )}

                {!membersLoading && (
                    <div className={styles.miniTableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Claim</th>
                                    <th>Client</th>
                                    <th>Value</th>
                                    <th>Agreement</th>
                                    {isAdmin && <th>Docs</th>}
                                    <th>Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {claims.length === 0 ? (
                                    <tr>
                                        <td colSpan={isAdmin ? 6 : 5}>
                                            <div className={styles.emptyState}>No claims in this omnibus.</div>
                                        </td>
                                    </tr>
                                ) : (
                                    claims.map((claim) => (
                                        <tr
                                            key={claim.id}
                                            className={`${styles.claimRow} ${
                                                detailClaim?.id === claim.id ? styles.selected : ''
                                            }`}
                                            onClick={() => openClaimDetail(claim)}
                                            title="View full captured claim record"
                                        >
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>{claim.funder_case_ref}</span>
                                                    <span className={styles.stackSecondary}>
                                                        {claim.agreement_number || 'No agreement no.'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        {formatClientName(
                                                            claim.first_name,
                                                            claim.last_name,
                                                            String(claim.funder_client_id),
                                                        )}
                                                        {(claim.client_claim_count || 1) > 1 && (
                                                            <span className={styles.multiClaimPill}>
                                                                {claim.client_claim_count} claims
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        {claim.email_address || 'No email'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        {formatMoney(claim.quantum_individual)}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        Loan {formatMoney(claim.loan_value)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        {formatDate(claim.agreement_start_date)}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        {claim.batch_group || '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            {isAdmin && (
                                                <td>
                                                    {claim.has_detail ? (
                                                        <span className={styles.docPill}>
                                                            <FaPaperclip /> {claim.document_count}
                                                        </span>
                                                    ) : (
                                                        <span className={styles.pendingPill}>Not captured</span>
                                                    )}
                                                </td>
                                            )}
                                            <td>{formatDate(claim.joined_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!membersLoading && memberStats && memberStats.claim_count > claims.length && (
                    <p className={styles.miniFooter}>
                        Showing the {claims.length} most recent of{' '}
                        {memberStats.claim_count.toLocaleString('en-GB')} claims — open the omnibus to search and page
                        through them all.
                    </p>
                )}
            </div>
        )
    }

    const importStats = useMemo(() => {
        if (!importResult) return []
        const stats: { label: string; value: number; tone?: 'warn' }[] = [
            { label: 'Claims imported', value: importResult.claims_imported },
            { label: 'Clients imported', value: importResult.clients_imported },
            { label: 'Already in an omnibus', value: importResult.claims_already_in_omnibus },
            { label: 'Groups created', value: importResult.groups_created },
            { label: 'Records captured', value: importResult.details_captured },
            { label: 'Documents attached', value: importResult.documents_captured },
        ]
        const warnings: { label: string; value: number; tone?: 'warn' }[] = [
            { label: 'Awaiting capture', value: importResult.details_remaining, tone: 'warn' },
            { label: 'No lender on claim', value: importResult.unassignable_claims, tone: 'warn' },
            { label: 'No introducer record', value: importResult.lead_data_missing, tone: 'warn' },
            { label: 'Capture errors', value: importResult.enrichment_errors, tone: 'warn' },
            { label: 'Failed to add', value: importResult.claims_failed, tone: 'warn' },
            { label: 'No longer eligible', value: importResult.no_longer_eligible, tone: 'warn' },
        ]
        return [...stats, ...warnings.filter((stat) => stat.value > 0)]
    }, [importResult])

    const lenderChartAllRows = useMemo(
        () => [...summary.claims_by_omnibus].sort((a, b) => b.claim_count - a.claim_count),
        [summary.claims_by_omnibus],
    )

    const lenderChartPageCount = Math.max(1, Math.ceil(lenderChartAllRows.length / LENDER_CHART_PAGE_SIZE))

    const lenderChartRows = useMemo(() => {
        const start = lenderChartPage * LENDER_CHART_PAGE_SIZE
        return lenderChartAllRows.slice(start, start + LENDER_CHART_PAGE_SIZE)
    }, [lenderChartAllRows, lenderChartPage])

    const claimsByLenderChart = useMemo(
        () => ({
            labels: lenderChartRows.map((r) => r.defendant),
            datasets: [
                {
                    label: 'Claims',
                    data: lenderChartRows.map((r) => r.claim_count),
                    backgroundColor: '#5b7089',
                    borderRadius: 3,
                    barPercentage: 0.78,
                    categoryPercentage: 0.72,
                },
                {
                    label: 'Clients',
                    data: lenderChartRows.map((r) => r.client_count),
                    backgroundColor: '#c3ccd8',
                    borderRadius: 3,
                    barPercentage: 0.78,
                    categoryPercentage: 0.72,
                },
            ],
        }),
        [lenderChartRows],
    )

    // Chart.js has no built-in value labels and the datalabels plugin isn't a
    // dependency here, so the counts are drawn onto the canvas directly.
    const barValueLabels = useMemo<Plugin<'bar'>>(
        () => ({
            id: 'omnibusBarValueLabels',
            afterDatasetsDraw(chart) {
                const { ctx } = chart
                ctx.save()
                ctx.font = "500 11px 'Poppins', sans-serif"
                ctx.textAlign = 'left'
                ctx.textBaseline = 'middle'
                chart.data.datasets.forEach((dataset, datasetIndex) => {
                    const meta = chart.getDatasetMeta(datasetIndex)
                    if (meta.hidden) return
                    ctx.fillStyle = datasetIndex === 0 ? '#334155' : '#94a3b8'
                    meta.data.forEach((element, index) => {
                        const value = Number(dataset.data[index])
                        if (!Number.isFinite(value) || value === 0) return
                        ctx.fillText(value.toLocaleString('en-GB'), element.x + 6, element.y)
                    })
                })
                ctx.restore()
            },
        }),
        [],
    )

    const multiClientChart = useMemo(() => {
        const multi = summary.clients_in_multiple_omnibuses
        const single = Math.max(summary.total_distinct_clients - multi, 0)
        return {
            labels: ['Single omnibus', 'Multiple omnibuses'],
            datasets: [
                {
                    data: [single, multi],
                    backgroundColor: ['#c3ccd8', '#5b7089'],
                    borderWidth: 0,
                },
            ],
        }
    }, [summary.clients_in_multiple_omnibuses, summary.total_distinct_clients])

    const clientsByCountChart = useMemo(() => {
        // Counts thin out sharply past four, so everything from five upwards is
        // collapsed into a single bucket rather than a tail of tiny bars.
        const MAX_BUCKET = 5
        const buckets = new Map<number, number>()
        for (const row of summary.clients_by_omnibus_count) {
            const key = Math.min(row.omnibus_count, MAX_BUCKET)
            buckets.set(key, (buckets.get(key) || 0) + row.client_count)
        }
        const rows = Array.from(buckets.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([omnibus_count, client_count]) => ({
                omnibus_count,
                client_count,
                label: omnibus_count >= MAX_BUCKET ? `${MAX_BUCKET}+ omnibuses` : `${omnibus_count} omnibuses`,
            }))

        if (!rows.length) {
            return {
                labels: ['No multi-omnibus clients'],
                datasets: [{ label: 'Clients', data: [0], backgroundColor: ['#e2e8f0'], borderRadius: 6 }],
            }
        }
        return {
            labels: rows.map((r) => r.label),
            datasets: [
                {
                    label: 'Clients',
                    data: rows.map((r) => r.client_count),
                    backgroundColor: rows.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                    borderRadius: 6,
                },
            ],
        }
    }, [summary.clients_by_omnibus_count])

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
            },
            scales: {
                x: {
                    ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } },
                    grid: { display: false },
                },
                y: {
                    beginAtZero: true,
                    ticks: { precision: 0 },
                },
            },
        }),
        [],
    )

    const lenderChartOptions = useMemo(() => {
        const rows = lenderChartRows
        return {
            indexAxis: 'y' as const,
            responsive: true,
            maintainAspectRatio: false,
            // Leaves room for the counts drawn past the end of each bar.
            layout: { padding: { right: 64 } },
            plugins: {
                legend: {
                    display: true,
                    position: 'top' as const,
                    align: 'end' as const,
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                        usePointStyle: true,
                        pointStyle: 'circle' as const,
                        font: { size: 11 },
                        color: '#64748b',
                        padding: 16,
                    },
                },
                tooltip: {
                    backgroundColor: '#1e293b',
                    padding: 10,
                    titleFont: { size: 12 },
                    bodyFont: { size: 11 },
                    callbacks: {
                        // Axis labels are truncated to stay readable, so the
                        // tooltip carries the full lender name and its ref.
                        title: (items: TooltipItem<'bar'>[]) => {
                            const row = rows[items[0]?.dataIndex]
                            return row ? `${row.defendant} · ${row.omnibus_ref}` : ''
                        },
                        label: (item: TooltipItem<'bar'>) => {
                            const value = item.parsed.x ?? 0
                            return `${item.dataset.label}: ${value.toLocaleString('en-GB')}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0,
                        font: { size: 11 },
                        color: '#94a3b8',
                        callback: (value: number | string) => Number(value).toLocaleString('en-GB'),
                    },
                    grid: { color: '#f1f5f9' },
                    border: { display: false },
                },
                y: {
                    ticks: {
                        autoSkip: false,
                        font: { size: 11 },
                        color: '#475569',
                        crossAlign: 'far' as const,
                        callback: (_value: unknown, index: number) => {
                            const label = rows[index]?.defendant ?? ''
                            return label.length > 28 ? `${label.slice(0, 27)}…` : label
                        },
                    },
                    grid: { display: false },
                    border: { display: false },
                },
            },
        }
    }, [lenderChartRows])

    const doughnutOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'bottom' as const },
            },
        }),
        [],
    )

    return (
        <div className={styles.wrap}>
            {isAdmin && (
                <section className={styles.importPanel}>
                    <button
                        type="button"
                        className={styles.panelToggle}
                        onClick={() => setImportOpen((open) => !open)}
                        aria-expanded={importOpen}
                    >
                        <span className={styles.panelToggleText}>
                            <span className={styles.importTitle}>Omnibus data</span>
                            <span className={styles.importBody}>Import, re-capture and reset tools</span>
                        </span>
                        <FaChevronDown className={`${styles.panelChevron} ${importOpen ? styles.open : ''}`} />
                    </button>

                    {importOpen && (
                        <div className={styles.panelContent}>
                            <p className={styles.importBody}>
                                Omnibus groups are built from funding-batch claims. You choose which batches to
                                import; audit-removed claims are always excluded, and a claim is never added to
                                an omnibus twice.
                            </p>

                            {importResult && (
                                <>
                                    <div className={styles.importStats}>
                                        {importStats.map((stat) => (
                                            <div
                                                key={stat.label}
                                                className={`${styles.importStat} ${stat.tone ? styles[stat.tone] : ''}`}
                                            >
                                                <span className={styles.importStatValue}>
                                                    {stat.value.toLocaleString('en-GB')}
                                                </span>
                                                <span className={styles.importStatLabel}>{stat.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className={styles.importReconcile}>
                                        {importResult.claims_in_omnibuses.toLocaleString('en-GB')} of{' '}
                                        {importResult.eligible_claims.toLocaleString('en-GB')} eligible claims are in an
                                        omnibus, covering {importResult.clients_in_omnibuses.toLocaleString('en-GB')}{' '}
                                        clients.
                                    </p>
                                    {importResult.details_remaining > 0 && (
                                        <div className={styles.importContinue}>
                                            <p className={styles.importReconcile}>
                                                {importResult.details_remaining.toLocaleString('en-GB')} claim(s) still need
                                                their record and documents captured. Capture runs in batches to keep each
                                                request short.
                                            </p>
                                            <button
                                                type="button"
                                                className={styles.primaryBtn}
                                                onClick={() => runImport(false)}
                                                disabled={importing || loading || previewLoading}
                                            >
                                                <FaSyncAlt /> Continue capture
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {emailImportResult && (
                                <div className={styles.emailResults}>
                                    <div className={styles.emailResultSummary}>
                                        <strong>Defendant email import</strong>
                                        <span className={styles.emailImported}>
                                            {emailImportResult.imported} imported
                                        </span>
                                        <span className={styles.emailDuplicate}>
                                            {emailImportResult.duplicate} duplicate
                                        </span>
                                        <span className={styles.emailFailed}>{emailImportResult.failed} failed</span>
                                    </div>
                                    {emailImportResult.files.length > 0 && (
                                        <ul className={styles.emailFileResults}>
                                            {emailImportResult.files.map((file, index) => {
                                                const groupLabels = (file.groups || []).map((group) =>
                                                    typeof group === 'string'
                                                        ? group
                                                        : group.omnibus_ref || group.defendant || group.name || 'Matched group',
                                                )
                                                const defendantLabels = (file.defendants || []).map((defendant) =>
                                                    typeof defendant === 'string'
                                                        ? defendant
                                                        : defendant.defendant || defendant.name || 'Matched defendant',
                                                )
                                                return (
                                                    <li key={`${file.filename}-${index}`} className={styles.emailFileResult}>
                                                        <span
                                                            className={`${styles.emailStatus} ${styles[file.status]}`}
                                                        >
                                                            {file.status}
                                                        </span>
                                                        <div>
                                                            <strong>{file.filename}</strong>
                                                            {(defendantLabels.length > 0 || groupLabels.length > 0) && (
                                                                <span className={styles.emailMatch}>
                                                                    {defendantLabels.length > 0 &&
                                                                        `Defendant: ${defendantLabels.join(', ')}`}
                                                                    {defendantLabels.length > 0 &&
                                                                        groupLabels.length > 0 &&
                                                                        ' · '}
                                                                    {groupLabels.length > 0 &&
                                                                        `Group: ${groupLabels.join(', ')}`}
                                                                </span>
                                                            )}
                                                            {file.message && (
                                                                <span className={styles.emailMessage}>{file.message}</span>
                                                            )}
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className={styles.actionRow}>
                                <div className={styles.actionCard}>
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        onClick={openImportModal}
                                        disabled={importing || loading || previewLoading}
                                    >
                                        <FaFileImport /> Import claims
                                    </button>
                                    <p className={styles.actionHint}>
                                        Pick exactly which funding batches to bring in, preview the result,
                                        then confirm. Lender groups are created as needed.
                                    </p>
                                </div>

                                <div className={styles.actionCard}>
                                    <label className={styles.emailFilePicker}>
                                        <FaEnvelope />
                                        <span>
                                            {emailFiles.length === 0
                                                ? 'Choose .eml files'
                                                : `${emailFiles.length} email${emailFiles.length === 1 ? '' : 's'} selected`}
                                        </span>
                                        <input
                                            ref={emailInputRef}
                                            type="file"
                                            accept=".eml,message/rfc822"
                                            multiple
                                            disabled={emailImporting}
                                            onChange={(event) =>
                                                setEmailFiles(
                                                    Array.from(event.target.files || []).filter((file) =>
                                                        file.name.toLowerCase().endsWith('.eml'),
                                                    ),
                                                )
                                            }
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        onClick={importDefendantEmails}
                                        disabled={emailImporting || emailFiles.length === 0}
                                    >
                                        <FaFileImport />{' '}
                                        {emailImporting ? 'Importing emails…' : 'Import defendant emails'}
                                    </button>
                                    <p className={styles.actionHint}>
                                        Imports one or more raw .eml files, deduplicates them and matches each thread to
                                        its canonical defendant omnibus.
                                    </p>
                                </div>

                                <div className={styles.actionCard}>
                                    <button
                                        type="button"
                                        className={styles.secondaryBtn}
                                        onClick={confirmRecapture}
                                        disabled={importing || loading}
                                    >
                                        <FaSyncAlt /> Re-capture data
                                    </button>
                                    <p className={styles.actionHint}>
                                        Re-reads every claim already in an omnibus and overwrites its stored record and
                                        document links with current values. Adds nothing new.
                                    </p>
                                </div>

                                <div className={styles.actionCard}>
                                    <button
                                        type="button"
                                        className={styles.dangerBtn}
                                        onClick={openReset}
                                        disabled={importing || loading || resetting}
                                    >
                                        <FaTrashAlt /> Reset omnibuses
                                    </button>
                                    <p className={styles.actionHint}>
                                        Empties or deletes omnibus groups so you can rebuild from scratch. Funding batches
                                        and introducer data are never touched.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={styles.summaryRow}>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{summary.total_groups}</span>
                    <span className={styles.summaryLabel}>Omnibus groups</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{summary.total_claims}</span>
                    <span className={styles.summaryLabel}>Active claims</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{summary.total_distinct_clients}</span>
                    <span className={styles.summaryLabel}>Distinct clients</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{summary.clients_in_multiple_omnibuses}</span>
                    <span className={styles.summaryLabel}>Clients in multiple omnibuses</span>
                </div>
            </div>

            <div className={styles.clientChartsPanel}>
                <button
                    type="button"
                    className={styles.clientChartsToggle}
                    onClick={() => setLenderChartOpen((open) => !open)}
                    aria-expanded={lenderChartOpen}
                >
                    <span className={styles.clientChartsToggleText}>
                        Claims and clients by lender
                        <span className={styles.clientChartsToggleHint}>
                            {lenderChartAllRows.length.toLocaleString('en-GB')} omnibus groups ranked by claim volume
                        </span>
                    </span>
                    <span className={styles.clientChartsToggleAction}>
                        {lenderChartOpen ? 'Hide' : 'Show'}
                        <FaChevronDown className={`${styles.panelChevron} ${lenderChartOpen ? styles.open : ''}`} />
                    </span>
                </button>

                {lenderChartOpen && (
                    <div className={styles.chartCard}>
                        <div className={styles.chartHeader}>
                            <div>
                                <h3 className={styles.chartTitle}>Claims and clients by lender</h3>
                                <span className={styles.chartHint}>
                                    Ranked by claim volume · showing{' '}
                                    {lenderChartAllRows.length === 0 ? 0 : lenderChartPage * LENDER_CHART_PAGE_SIZE + 1}
                                    –{lenderChartPage * LENDER_CHART_PAGE_SIZE + lenderChartRows.length} of{' '}
                                    {lenderChartAllRows.length}
                                </span>
                            </div>
                        </div>
                        <div className={`${styles.chartBody} ${styles.chartBodyLenders}`}>
                            {lenderChartRows.length === 0 ? (
                                <div className={styles.emptyState}>No claim data yet</div>
                            ) : (
                                <Bar
                                    data={claimsByLenderChart}
                                    options={lenderChartOptions}
                                    plugins={[barValueLabels]}
                                />
                            )}
                        </div>

                        {lenderChartPageCount > 1 && (
                            <div className={styles.chartPager}>
                                <button
                                    type="button"
                                    className={styles.pagerBtn}
                                    onClick={() => setLenderChartPage((p) => Math.max(0, p - 1))}
                                    disabled={lenderChartPage === 0}
                                    aria-label="Previous lenders"
                                >
                                    <FaChevronLeft /> Previous
                                </button>
                                <span className={styles.pagerLabel}>
                                    Page {lenderChartPage + 1} of {lenderChartPageCount}
                                </span>
                                <button
                                    type="button"
                                    className={styles.pagerBtn}
                                    onClick={() => setLenderChartPage((p) => Math.min(lenderChartPageCount - 1, p + 1))}
                                    disabled={lenderChartPage >= lenderChartPageCount - 1}
                                    aria-label="Next lenders"
                                >
                                    Next <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.clientChartsPanel}>
                <button
                    type="button"
                    className={styles.clientChartsToggle}
                    onClick={() => setMultiClientChartsOpen((open) => !open)}
                    aria-expanded={multiClientChartsOpen}
                >
                    <span className={styles.clientChartsToggleText}>
                        Multi-omnibus client charts
                        <span className={styles.clientChartsToggleHint}>
                            {summary.clients_in_multiple_omnibuses.toLocaleString('en-GB')} clients across multiple
                            omnibuses
                        </span>
                    </span>
                    <span className={styles.clientChartsToggleAction}>
                        {multiClientChartsOpen ? 'Hide' : 'Show'}
                        <FaChevronDown
                            className={`${styles.panelChevron} ${multiClientChartsOpen ? styles.open : ''}`}
                        />
                    </span>
                </button>

                {multiClientChartsOpen && (
                    <div className={styles.chartsRow}>
                        <div className={styles.chartCard}>
                            <div className={styles.chartHeader}>
                                <h3 className={styles.chartTitle}>Clients across omnibuses</h3>
                            </div>
                            <div className={styles.chartBody}>
                                {summary.total_distinct_clients === 0 ? (
                                    <div className={styles.emptyState}>No client data yet</div>
                                ) : (
                                    <Doughnut data={multiClientChart} options={doughnutOptions} />
                                )}
                            </div>
                        </div>
                        <div className={styles.chartCard}>
                            <div className={styles.chartHeader}>
                                <h3 className={styles.chartTitle}>Multi-omnibus client spread</h3>
                            </div>
                            <div className={styles.chartBody}>
                                <Bar data={clientsByCountChart} options={chartOptions} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    placeholder="Filter by lender, omnibus ref, or status…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <select
                    className={styles.toolbarSelect}
                    value={groupSort}
                    onChange={(e) => setGroupSort(e.target.value as GroupSort)}
                    aria-label="Sort omnibus groups"
                >
                    {GROUP_SORTS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={captureFilter}
                    onChange={(e) => setCaptureFilter(e.target.value)}
                    aria-label="Filter by capture state"
                >
                    {CAPTURE_FILTERS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter by status"
                >
                    <option value="">Any status</option>
                    {statusOptions.map((status) => (
                        <option key={status} value={status}>
                            {status}
                        </option>
                    ))}
                </select>
                <button type="button" className={styles.refreshBtn} onClick={() => fetchGroups()} disabled={loading}>
                    <FaSyncAlt /> Refresh
                </button>
                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={exportGroups}
                    disabled={loading || exporting || groups.length === 0}
                    title="Export all omnibus groups"
                >
                    <FaFileExcel /> {exporting ? 'Preparing…' : 'Export Excel'}
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {filteredGroups.length.toLocaleString('en-GB')} of {groups.length.toLocaleString('en-GB')}{' '}
                omnibus groups
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Omnibus</th>
                            <th>Volume</th>
                            <th>Value</th>
                            <th>Data captured</th>
                            <th>Status</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {filteredGroups.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <div className={styles.emptyState}>
                                        {loading
                                            ? 'Loading…'
                                            : groups.length === 0
                                              ? 'No omnibus groups yet. Groups will appear once claims are assigned.'
                                              : 'No groups match this filter.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredGroups.map((group) => (
                                <React.Fragment key={group.id}>
                                <tr
                                    className={selectedId === group.id ? styles.selected : ''}
                                    onClick={() => setSelectedId((current) => (current === group.id ? null : group.id))}
                                >
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                <FaChevronRight
                                                    className={`${styles.rowChevron} ${
                                                        selectedId === group.id ? styles.rowChevronOpen : ''
                                                    }`}
                                                />
                                                {group.defendant}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                <span className={styles.refCode}>{group.omnibus_ref}</span>
                                                {group.sequence_no > 1 && (
                                                    <span className={styles.stackMeta}>· seq {group.sequence_no}</span>
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {group.claim_count.toLocaleString('en-GB')} claims
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {group.client_count.toLocaleString('en-GB')} clients
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.stackPrimary}>
                                            {group.total_quantum === null ? '—' : formatMoney(group.total_quantum)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.stackCell}>
                                            <span className={styles.stackPrimary}>
                                                {group.claim_count === 0
                                                    ? '—'
                                                    : `${Math.round(
                                                          (group.captured_count / group.claim_count) * 100,
                                                      )}%`}
                                            </span>
                                            <span className={styles.stackSecondary}>
                                                {group.captured_count.toLocaleString('en-GB')} of{' '}
                                                {group.claim_count.toLocaleString('en-GB')}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusPill} ${styles[group.status] || ''}`}>
                                            {group.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <button
                                                type="button"
                                                className={styles.viewBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/omnibus-claims/${group.id}`)
                                                }}
                                            >
                                                View <FaChevronRight />
                                            </button>
                                            {group.claim_count > group.captured_count && (
                                                <button
                                                    type="button"
                                                    className={styles.captureBtn}
                                                    disabled={capturingIds.includes(group.id)}
                                                    title={`Capture ${(
                                                        group.claim_count - group.captured_count
                                                    ).toLocaleString('en-GB')} outstanding claim(s)`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        captureGroup(group)
                                                    }}
                                                >
                                                    {capturingIds.includes(group.id) ? (
                                                        <span className={styles.captureBusy}>Capturing…</span>
                                                    ) : (
                                                        // Swapped on hover so the button stays
                                                        // as narrow as the View button above it.
                                                        <span className={styles.captureSwap}>
                                                            <span className={styles.captureIdle}>Capture</span>
                                                            <span className={styles.captureHover}>
                                                                {(
                                                                    group.claim_count - group.captured_count
                                                                ).toLocaleString('en-GB')}
                                                            </span>
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {selectedId === group.id && (
                                    <tr className={styles.expandedRow}>
                                        <td colSpan={6}>{renderGroupPreview(group)}</td>
                                    </tr>
                                )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Send-log spreadsheet upload — same tool as Admin › Omnibus Comms
                Upload, surfaced here so admins can record new comms without
                leaving the omnibus page. Sentinel admins only. */}
            {isAdmin && (
                <section className={`${styles.importPanel} ${styles.commsUploadPanel}`}>
                    <button
                        type="button"
                        className={styles.panelToggle}
                        onClick={() => setCommsUploadOpen((open) => !open)}
                        aria-expanded={commsUploadOpen}
                    >
                        <span className={styles.panelToggleText}>
                            <span className={styles.importTitle}>Client communications upload</span>
                            <span className={styles.importBody}>
                                Record emails and SMS sent to clients from a conversations.csv export
                            </span>
                        </span>
                        <FaChevronDown className={`${styles.panelChevron} ${commsUploadOpen ? styles.open : ''}`} />
                    </button>
                    {commsUploadOpen && (
                        <div className={styles.panelContent}>
                            <OmnibusCommsUploadView embedded />
                        </div>
                    )}
                </section>
            )}

            {resetOpen && (
                <div className={`${styles.drawerOverlay} ${styles.centered}`} onClick={closeReset}>
                    <div
                        className={styles.previewModal}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="omnibus-reset-title"
                    >
                        <div className={styles.previewHeader}>
                            <div>
                                <h3 id="omnibus-reset-title" className={styles.previewTitle}>
                                    Reset omnibus data
                                </h3>
                                <p className={styles.previewSubtitle}>
                                    This only removes omnibus records. Funding batches, funder claims and clients, and
                                    all introducer data stay exactly as they are — re-running the import rebuilds
                                    anything removed here.
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={closeReset}
                                disabled={resetting}
                                aria-label="Close reset dialog"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.resetBody}>
                            <div className={styles.resetModes}>
                                {RESET_MODES.map((option) => (
                                    <label
                                        key={option.value}
                                        className={`${styles.resetMode} ${
                                            resetMode === option.value ? styles.active : ''
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="omnibus-reset-mode"
                                            value={option.value}
                                            checked={resetMode === option.value}
                                            onChange={() => {
                                                setResetMode(option.value)
                                                setResetConfirmText('')
                                            }}
                                            disabled={resetting}
                                        />
                                        <span>
                                            <span className={styles.resetModeLabel}>{option.label}</span>
                                            <span className={styles.resetModeHint}>{option.hint}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {resetMode !== 'wipe_all' && (
                                <label className={styles.resetField}>
                                    <span className={styles.resetFieldLabel}>Lender omnibus</span>
                                    <select
                                        className={styles.resetSelect}
                                        value={resetGroupId ?? ''}
                                        onChange={(e) =>
                                            setResetGroupId(e.target.value ? Number(e.target.value) : null)
                                        }
                                        disabled={resetting}
                                    >
                                        <option value="">Select a lender…</option>
                                        {groups.map((group) => (
                                            <option key={group.id} value={group.id}>
                                                {group.defendant} ({group.omnibus_ref}) —{' '}
                                                {group.claim_count.toLocaleString('en-GB')} claims
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            {resetMode === 'wipe_all' && (
                                <label className={styles.resetField}>
                                    <span className={styles.resetFieldLabel}>
                                        Type RESET to confirm wiping every omnibus
                                    </span>
                                    <input
                                        className={styles.resetInput}
                                        type="text"
                                        value={resetConfirmText}
                                        onChange={(e) => setResetConfirmText(e.target.value)}
                                        placeholder="RESET"
                                        disabled={resetting}
                                    />
                                </label>
                            )}

                            {resetPreviewLoading && (
                                <div className={styles.emptyState}>Checking what will be removed…</div>
                            )}

                            {!resetPreviewLoading && resetPreview && (
                                <div className={styles.resetSummary}>
                                    <span className={styles.resetSummaryTitle}>This will remove</span>
                                    <div className={styles.previewStats}>
                                        <div className={styles.previewStat}>
                                            <span className={styles.importStatValue}>
                                                {resetPreview.claims.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.importStatLabel}>Claims</span>
                                        </div>
                                        <div className={styles.previewStat}>
                                            <span className={styles.importStatValue}>
                                                {resetPreview.clients.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.importStatLabel}>Clients</span>
                                        </div>
                                        <div className={styles.previewStat}>
                                            <span className={styles.importStatValue}>
                                                {resetPreview.details.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.importStatLabel}>Captured records</span>
                                        </div>
                                        <div className={styles.previewStat}>
                                            <span className={styles.importStatValue}>
                                                {resetPreview.documents.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.importStatLabel}>Document links</span>
                                        </div>
                                        <div className={styles.previewStat}>
                                            <span className={styles.importStatValue}>
                                                {resetPreview.groups_deleted.toLocaleString('en-GB')}
                                            </span>
                                            <span className={styles.importStatLabel}>Groups deleted</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={styles.previewFooter}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={closeReset}
                                disabled={resetting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.dangerSolidBtn}
                                onClick={runReset}
                                disabled={
                                    resetting ||
                                    resetPreviewLoading ||
                                    (resetMode === 'wipe_all' ? resetConfirmText !== 'RESET' : resetGroupId == null)
                                }
                            >
                                <FaTrashAlt />{' '}
                                {resetMode === 'clear_group'
                                    ? 'Empty omnibus'
                                    : resetMode === 'delete_group'
                                      ? 'Delete omnibus'
                                      : 'Wipe all omnibuses'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ClaimDetailDrawer claim={detailClaim} onClose={closeClaimDetail} />

            {previewOpen && (
                <div className={`${styles.drawerOverlay} ${styles.centered}`} onClick={closeImportPreview}>
                    <div
                        className={styles.previewModal}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="omnibus-import-preview-title"
                    >
                        <div className={styles.previewHeader}>
                            <div>
                                <h3 id="omnibus-import-preview-title" className={styles.previewTitle}>
                                    {importStep === 'select' ? 'Select batches to import' : 'Import preview'}
                                </h3>
                                <p className={styles.previewSubtitle}>
                                    {importStep === 'select'
                                        ? 'Tick the funding batches to bring into the omnibus system. Audit-removed claims are always excluded. Nothing is written until you confirm on the next step.'
                                        : 'What the selected batches would add. Nothing is written until you confirm.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={closeImportPreview}
                                disabled={importing}
                                aria-label="Close import"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {importStep === 'select' && (
                            <>
                                {importBatchesLoading && (
                                    <div className={styles.emptyState}>Loading funding batches…</div>
                                )}
                                {!importBatchesLoading && (
                                    <>
                                        <div className={styles.previewTableWrap}>
                                            <table className={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th aria-label="Select" />
                                                        <th>NERA</th>
                                                        <th>Batch</th>
                                                        <th>Status</th>
                                                        <th>Claims</th>
                                                        <th>Imported</th>
                                                        <th>To import</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {importBatches.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={7}>
                                                                <div className={styles.emptyState}>
                                                                    No funding batches found.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        importBatches.map((batch) => {
                                                            const fullyImported = batch.claims_to_import === 0
                                                            return (
                                                                <tr
                                                                    key={batch.batch_group}
                                                                    className={fullyImported ? '' : styles.selectableRow}
                                                                    onClick={() => {
                                                                        if (!fullyImported) toggleBatchSelection(batch.batch_group)
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedBatches.has(batch.batch_group)}
                                                                            disabled={fullyImported}
                                                                            onClick={(event) => event.stopPropagation()}
                                                                            onChange={() => toggleBatchSelection(batch.batch_group)}
                                                                            aria-label={`Select ${batch.batch_group}`}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <span className={styles.refCode}>
                                                                            {batch.nera_id || '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className={styles.stackCell}>
                                                                            <span className={styles.stackPrimary}>
                                                                                <span className={styles.refCode}>{batch.batch_group}</span>
                                                                            </span>
                                                                            <span className={styles.stackSecondary}>
                                                                                {batch.introducers || '—'}
                                                                                {batch.is_funder_additional ? ' · Funder Additional' : ''}
                                                                                {batch.hidden ? ' · Hidden' : ''}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className={`${styles.statusPill} ${
                                                                                batch.batch_status === 'API transfer completed'
                                                                                    ? styles.open
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            {IMPORT_STATUS_LABELS[batch.batch_status] || batch.batch_status}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className={styles.stackCell}>
                                                                            <span className={styles.stackPrimary}>
                                                                                {batch.funding_claims.toLocaleString('en-GB')}
                                                                            </span>
                                                                            <span className={styles.stackSecondary}>
                                                                                {batch.funding_clients.toLocaleString('en-GB')} clients
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{batch.claims_in_omnibus.toLocaleString('en-GB')}</td>
                                                                    <td>
                                                                        {fullyImported ? (
                                                                            <span className={styles.statusPill}>Done</span>
                                                                        ) : (
                                                                            <span className={styles.stackPrimary}>
                                                                                {batch.claims_to_import.toLocaleString('en-GB')}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className={styles.previewFooter}>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtn}
                                                onClick={closeImportPreview}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.primaryBtn}
                                                onClick={goToImportPreview}
                                                disabled={selectedBatches.size === 0}
                                            >
                                                <FaFileImport /> Preview{' '}
                                                {selectedBatches.size > 0
                                                    ? `${selectedBatches.size} batch${selectedBatches.size === 1 ? '' : 'es'}`
                                                    : 'import'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {importStep === 'preview' && previewLoading && (
                            <div className={styles.emptyState}>Calculating what would be imported…</div>
                        )}

                        {importStep === 'preview' && !previewLoading && preview && (
                            <>
                                <div className={styles.previewStats}>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.claims_to_import.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Claims to add</span>
                                    </div>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.clients_to_import.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Clients to add</span>
                                    </div>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.groups_to_create.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Groups to create</span>
                                    </div>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.claims_already_in_omnibus.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Already assigned</span>
                                    </div>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.eligible_claims.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Eligible total</span>
                                    </div>
                                    <div className={styles.previewStat}>
                                        <span className={styles.importStatValue}>
                                            {preview.lenders_touched.toLocaleString('en-GB')}
                                        </span>
                                        <span className={styles.importStatLabel}>Lenders affected</span>
                                    </div>
                                </div>

                                {preview.unassignable_claims > 0 && (
                                    <p className={styles.previewNote}>
                                        {preview.unassignable_claims.toLocaleString('en-GB')} funding-batch claim(s)
                                        have no lender and will be skipped.
                                    </p>
                                )}

                                <div className={styles.previewTableWrap}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Lender</th>
                                                <th>To add</th>
                                                <th>Already in</th>
                                                <th>Group</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.by_lender.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <div className={styles.emptyState}>
                                                            No eligible claims found.
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                preview.by_lender.map((row) => (
                                                    <tr key={row.defendant}>
                                                        <td>
                                                            <div className={styles.stackCell}>
                                                                <span className={styles.stackPrimary}>
                                                                    {row.defendant}
                                                                </span>
                                                                <span className={styles.stackSecondary}>
                                                                    {row.omnibus_ref ? (
                                                                        <span className={styles.refCode}>
                                                                            {row.omnibus_ref}
                                                                        </span>
                                                                    ) : (
                                                                        'Ref assigned on create'
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className={styles.stackCell}>
                                                                <span className={styles.stackPrimary}>
                                                                    {row.claims_to_import.toLocaleString('en-GB')}{' '}
                                                                    claims
                                                                </span>
                                                                <span className={styles.stackSecondary}>
                                                                    {row.clients_to_import.toLocaleString('en-GB')}{' '}
                                                                    clients
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>{row.claims_already_in.toLocaleString('en-GB')}</td>
                                                        <td>
                                                            {row.group_exists ? (
                                                                <span className={styles.statusPill}>Exists</span>
                                                            ) : (
                                                                <span className={`${styles.statusPill} ${styles.open}`}>
                                                                    Will create
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className={styles.previewFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryBtn}
                                        onClick={() => setImportStep('select')}
                                        disabled={importing}
                                    >
                                        Back to selection
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        onClick={() => runImport(false, Array.from(selectedBatches))}
                                        disabled={importing || previewLoading || preview.claims_to_import === 0}
                                    >
                                        <FaFileImport />{' '}
                                        {`Import ${preview.claims_to_import.toLocaleString('en-GB')} claims`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
